import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { venuesAPI } from '../services/api';
import { Venue } from '../types/venue';
import VenueCard from '../components/venues/VenueCard';
import VenueModal from '../components/venues/VenueModal';
import DeleteConfirmModal from '../components/ui/DeleteConfirmModal';
import { PlusIcon } from '@heroicons/react/24/outline';

const Venues: React.FC = () => {
  usePageTitle('Pályák');
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [venueToDelete, setVenueToDelete] = useState<number | null>(null);

  useEffect(() => {
    loadVenues();
  }, []);

  const loadVenues = async () => {
    try {
      const data = await venuesAPI.getAllVenues();
      setVenues(data);
    } catch (error) {
      console.error('Failed to load venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedVenue(null);
    setIsModalOpen(true);
  };

  const handleEdit = (venue: Venue) => {
    setSelectedVenue(venue);
    setIsModalOpen(true);
  };

  const handleSave = async (data: any) => {
    try {
      if (selectedVenue) {
        await venuesAPI.updateVenue(selectedVenue.id, {
          ...data,
          isActive: true,
        });
      } else {
        await venuesAPI.createVenue(data);
      }
      setIsModalOpen(false);
      loadVenues();
    } catch (error) {
      console.error('Failed to save venue:', error);
    }
  };

  const handleDelete = async () => {
    if (venueToDelete === null) return;

    try {
      await venuesAPI.deleteVenue(venueToDelete);
      setVenueToDelete(null);
      loadVenues();
    } catch (error) {
      console.error('Failed to delete venue:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Betöltés...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Pályák</h1>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Új pálya
        </button>
      </div>

      {venues.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">Még nincsenek pályák hozzáadva.</p>
          <button
            onClick={handleCreate}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Első pálya hozzáadása
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {venues.map((venue) => (
            <VenueCard
              key={venue.id}
              venue={venue}
              onEdit={handleEdit}
              onDelete={(id) => setVenueToDelete(id)}
            />
          ))}
        </div>
      )}

      <VenueModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        venue={selectedVenue}
      />

      <DeleteConfirmModal
        isOpen={venueToDelete !== null}
        onClose={() => setVenueToDelete(null)}
        onConfirm={handleDelete}
        title="Pálya törlése"
        message="Biztosan törölni szeretnéd ezt a pályát?"
        itemName={venues.find(v => v.id === venueToDelete)?.name || ''}
      />
    </div>
  );
};

export default Venues;

